import { FC, useState } from 'react';

import cn from 'classnames';
import { UPLOAD_ATTACHMENTS_FORM } from 'modules/account/contact/forms/schema';
import * as Progress from '@radix-ui/react-progress';

import UploadListItem from './UploadListItem';

type UploadListProps = {
  list: UPLOAD_ATTACHMENTS_FORM['files'];
  onDelete: (_id: string) => void;
  defaultNumberOfFilesToShow?: number;
  progressCount: number;
  isUploading: boolean;
};

const UploadList: FC<UploadListProps> = ({
  list = [],
  onDelete,
  defaultNumberOfFilesToShow = 3,
  progressCount = 0,
  isUploading = false,
}) => {
  const [numberToShow, setNumberToShow] = useState(defaultNumberOfFilesToShow);
  const [showAll, setShowAll] = useState(false);

  const showAllItems = () => {
    setNumberToShow((prevVal) => prevVal + (list.length - numberToShow));
    setShowAll(true);
  };

  const hideAllItems = () => {
    setNumberToShow(defaultNumberOfFilesToShow);
    setShowAll(false);
  };

  const listNotEmpty = !!list?.length;

  return (
    <div
      className={cn(
        'w-full',
        'border-0',
        'border-t',
        'border-t-[#E4EDF0]',
        'pt-[30px]',
        'flex',
        'flex-col',
        'gap-3',
        'mt-[37px]',
      )}
    >
      <div className="flex text-sm text-secondary-dark items-center justify-between">
        {<div>{`${list.length ?? 0} files uploaded`}</div>}
        {isUploading && (
          <div className="flex items-center gap-2 text-xs">
            <Progress.Root
              className={cn(
                'relative rounded-[6px] overflow-hidden h-[6px] w-20 bg-separator-primary',
              )}
            >
              <Progress.Indicator
                style={{ transform: `translateX(-${100 - progressCount}%)` }}
                className={cn(
                  'bg-primary-base w-full h-full transition-transform',
                  'duration-700 ease-[cubic-bezier(0.65, 0, 0.35, 1)]',
                )}
              />
            </Progress.Root>
            <div className="text-secondary-base">{progressCount} %</div>
          </div>
        )}
      </div>
      {listNotEmpty &&
        list
          .slice(0, numberToShow)
          .map(
            ({ fileName, id, fileUrl, fileSize, fileThumbnail, fileType }) => (
              <UploadListItem
                key={id}
                item={{
                  fileName,
                  fileUrl,
                  id,
                  fileSize,
                  fileThumbnail,
                  fileType,
                }}
                onDelete={onDelete}
              />
            ),
          )}

      <div className="w-full text-center">
        {!showAll && list.length > numberToShow && (
          <button type="button" onClick={showAllItems}>
            {`+ ${list.length - numberToShow} more files`}
          </button>
        )}
        {showAll && list.length >= numberToShow && (
          <button type="button" onClick={hideAllItems}>
            Hide Items
          </button>
        )}
      </div>
    </div>
  );
};

export default UploadList;
